import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import RecitationSelectionPanel from './RecitationSelectionPanel';
import SelectedRecitationPanel from './SelectedRecitationPanel';
import StudentSelectionPanel from './StudentSelectionPanel';
import {
  getAssignmentBookList,
  getAssignmentLessonList,
  getAssignmentRecitationList,
} from '../../../../api/utils/lms/recitation/api';
import {
  BookNode,
  SelectedAssignment,
} from '../../../../pages/lms/recitation-mgmt/CreateRecitation';
import { AssignmentType } from '../../../../constants/types/lms/type';

type AssignmentStep = 'ASSIGNMENT' | 'STUDENT';

/**
 * 개편 LMS (교사) - AI인증제 관리 > AI인증제 내주기 페이지 > 콘텐츠 영역
 */
const CreateRecitationWrapper = () => {
  const { classId } = useParams();

  const [bookNodes, setBookNodes] = useState<BookNode[]>([]);
  const [selectedAssignmentIds, setSelectedAssignmentIds] = useState<
    Set<number>
  >(new Set());

  const [openedBookIds, setOpenedBookIds] = useState<Set<string>>(new Set());
  const [openedLessonIds, setOpenedLessonIds] = useState<Set<string>>(
    new Set(),
  );

  const [step, setStep] = useState<AssignmentStep>('ASSIGNMENT');

  useEffect(() => {
    const fetchInitialBooks = async () => {
      const booksRecitation = await getAssignmentBookList({
        classId,
        type: 'JEBS_AI_RECITATION',
      });

      const booksTotal = [
        ...(booksRecitation ?? []).map((title) => ({
          title,
          type: AssignmentType.JEBS_AI_RECITATION,
        })),
      ].sort((a, b) =>
        a.title.localeCompare(b.title, undefined, { numeric: true }),
      );

      const books = booksTotal.map((book) => ({
        id: book.type + '-' + Number(book.title.match(/\d+$/)?.[0]),
        title: book.title,
        type: book.type,
      }));

      if (books.length === 0) {
        setBookNodes([]);
        return;
      }

      const firstBook = books[0];
      const firstBookLessons = await getAssignmentLessonList({
        classId,
        type: firstBook.type,
        book: firstBook.title,
      });

      const bookNodes: BookNode[] = books.map((book) =>
        book === firstBook
          ? {
              ...book,
              isLessonLoaded: true,
              lessons: firstBookLessons.map((lesson) => ({
                id: book.id + '-' + Number(lesson.match(/\d+$/)?.[0]),
                bookId: book.id,
                title: lesson,
              })),
            }
          : book,
      );

      setBookNodes(bookNodes);

      setOpenedBookIds(new Set([firstBook.id]));
    };

    fetchInitialBooks();
  }, [classId]);

  const selectedAssignments = useMemo<SelectedAssignment[]>(() => {
    return bookNodes.flatMap(
      (book) =>
        book.lessons?.flatMap(
          (lesson) =>
            lesson.assignments
              ?.filter((assignment) => selectedAssignmentIds.has(assignment.id))
              .map((assignment) => ({
                ...assignment,
                bookTitle: book.title,
                lessonTitle: lesson.title,
              })) ?? [],
        ) ?? [],
    );
  }, [bookNodes, selectedAssignmentIds]);

  const handleBookClick = async (bookId: string) => {
    const targetBook = bookNodes.find((book) => book.id === bookId);

    if (!targetBook || targetBook.isLessonLoaded) return;

    const lessons = await getAssignmentLessonList({
      classId,
      type: targetBook.type,
      book: targetBook.title,
    });

    setBookNodes((prev) =>
      prev.map((book) =>
        book.id === bookId
          ? {
              ...book,
              isLessonLoaded: true,
              lessons: lessons.map((lesson) => ({
                id: book.id + '-' + Number(lesson.match(/\d+$/)?.[0]),
                bookId: book.id,
                title: lesson,
              })),
            }
          : book,
      ),
    );
  };

  const handleLessonClick = async (bookId: string, lessonId: string) => {
    const targetBook = bookNodes.find((book) => book.id === bookId);
    const targetLesson = targetBook?.lessons?.find(
      (lesson) => lesson.id === lessonId,
    );

    if (!targetLesson || targetLesson.isAssignmentLoaded) return;

    const assignments = await getAssignmentRecitationList({
      classId,
      type: targetBook.type,
      book: targetBook.title,
      lesson: targetLesson.title,
    });

    setBookNodes((prev) =>
      prev.map((book) =>
        book.id === bookId
          ? {
              ...book,
              lessons: book.lessons?.map((lesson) =>
                lesson.id === lessonId
                  ? {
                      ...lesson,
                      isAssignmentLoaded: true,
                      assignments,
                    }
                  : lesson,
              ),
            }
          : book,
      ),
    );
  };

  const handleBookSelect = async (bookId: string) => {
    const targetBook = bookNodes.find((book) => book.id === bookId);
    if (!targetBook) return;

    let targetBookNode = targetBook;

    if (!targetBookNode.isLessonLoaded) {
      const lessons = await getAssignmentLessonList({
        classId,
        type: targetBook.type,
        book: targetBook.title,
      });

      targetBookNode = {
        ...targetBookNode,
        isLessonLoaded: true,
        lessons: lessons.map((lesson) => ({
          id: targetBook.id + '-' + Number(lesson.match(/\d+$/)?.[0]),
          bookId: targetBook.id,
          title: lesson,
        })),
      };
    }

    const lessonsWithAssignments = await Promise.all(
      (targetBookNode.lessons ?? []).map(async (lesson) => {
        if (lesson.isAssignmentLoaded) return lesson;

        const assignments = await getAssignmentRecitationList({
          classId,
          type: targetBook.type,
          book: targetBook.title,
          lesson: lesson.title,
        });

        return {
          ...lesson,
          isAssignmentLoaded: true,
          assignments,
        };
      }),
    );

    setBookNodes((prev) =>
      prev.map((book) =>
        book.id === bookId
          ? {
              ...book,
              isLessonLoaded: true,
              lessons: lessonsWithAssignments,
            }
          : book,
      ),
    );

    const assignmentIds = lessonsWithAssignments.flatMap(
      (lesson) => lesson.assignments?.map((assignment) => assignment.id) ?? [],
    );

    setSelectedAssignmentIds((prev) => {
      const next = new Set(prev);
      const allSelected = assignmentIds.every((id) => next.has(id));

      assignmentIds.forEach((id) => {
        if (allSelected) {
          next.delete(id);
        } else {
          next.add(id);
        }
      });

      return next;
    });

    // Book 열기
    setOpenedBookIds((prev) => {
      const next = new Set(prev);
      next.add(bookId);
      return next;
    });

    // 하위 Lesson도 전부 열기
    setOpenedLessonIds((prev) => {
      const next = new Set(prev);

      lessonsWithAssignments.forEach((lesson) => {
        next.add(lesson.id);
      });

      return next;
    });
  };

  const handleLessonSelect = async (bookId: string, lessonId: string) => {
    const targetBook = bookNodes.find((book) => book.id === bookId);
    const targetLesson = targetBook?.lessons?.find(
      (lesson) => lesson.id === lessonId,
    );

    if (!targetBook || !targetLesson) return;

    let assignments = targetLesson.assignments;

    if (!targetLesson.isAssignmentLoaded) {
      assignments = await getAssignmentRecitationList({
        classId,
        type: targetBook.type,
        book: targetBook.title,
        lesson: targetLesson.title,
      });

      setBookNodes((prev) =>
        prev.map((book) =>
          book.id === bookId
            ? {
                ...book,
                lessons: book.lessons?.map((lesson) =>
                  lesson.id === lessonId
                    ? {
                        ...lesson,
                        isAssignmentLoaded: true,
                        assignments,
                      }
                    : lesson,
                ),
              }
            : book,
        ),
      );
    }

    const assignmentIds = assignments?.map((assignment) => assignment.id) ?? [];

    setSelectedAssignmentIds((prev) => {
      const next = new Set(prev);
      const allSelected = assignmentIds.every((id) => next.has(id));

      assignmentIds.forEach((id) => {
        if (allSelected) {
          next.delete(id);
        } else {
          next.add(id);
        }
      });

      return next;
    });

    setOpenedLessonIds((prev) => {
      const next = new Set(prev);
      next.add(lessonId);
      return next;
    });
  };

  const handleBookOpen = async (bookId: string) => {
    const isOpened = openedBookIds.has(bookId);

    if (isOpened) {
      const targetBook = bookNodes.find((book) => book.id === bookId);
      const lessonIds = targetBook?.lessons?.map((lesson) => lesson.id) ?? [];

      // Book 닫기
      setOpenedBookIds((prev) => {
        const next = new Set(prev);
        next.delete(bookId);
        return next;
      });

      // 해당 Book 하위 Lesson들도 닫기
      setOpenedLessonIds((prev) => {
        const next = new Set(prev);

        lessonIds.forEach((lessonId) => {
          next.delete(lessonId);
        });

        return next;
      });

      return;
    }

    await handleBookClick(bookId);

    setOpenedBookIds((prev) => {
      const next = new Set(prev);
      next.add(bookId);
      return next;
    });
  };

  const handleLessonOpen = async (bookId: string, lessonId: string) => {
    const isOpened = openedLessonIds.has(lessonId);

    if (isOpened) {
      setOpenedLessonIds((prev) => {
        const next = new Set(prev);
        next.delete(lessonId);
        return next;
      });

      return;
    }

    await handleLessonClick(bookId, lessonId);

    setOpenedLessonIds((prev) => {
      const next = new Set(prev);
      next.add(lessonId);
      return next;
    });
  };

  const handleAssignmentSelect = (assignmentId: number) => {
    setSelectedAssignmentIds((prev) => {
      const next = new Set(prev);

      if (next.has(assignmentId)) {
        next.delete(assignmentId);
      } else {
        next.add(assignmentId);
      }

      return next;
    });
  };

  const removeSelectedAssignment = (assignmentId: number) => {
    setSelectedAssignmentIds((prev) => {
      const next = new Set(prev);
      next.delete(assignmentId);
      return next;
    });
  };

  const handleStudentSelectClick = (
    selectedAssignments: SelectedAssignment[],
  ) => {
    if (selectedAssignments.length === 0) return;

    setStep('STUDENT');
  };

  return (
    <>
      {step === 'ASSIGNMENT' ? (
        <div className="my-5 grid grid-cols-1 gap-4 px-3 xl:grid-cols-[1fr_40%] xl:px-0">
          <RecitationSelectionPanel
            bookNodes={bookNodes}
            selectedAssignmentIds={selectedAssignmentIds}
            openedBookIds={openedBookIds}
            openedLessonIds={openedLessonIds}
            onBookOpen={handleBookOpen}
            onLessonOpen={handleLessonOpen}
            onBookSelect={handleBookSelect}
            onLessonSelect={handleLessonSelect}
            onAssignmentSelect={handleAssignmentSelect}
          />

          <SelectedRecitationPanel
            selectedAssignments={selectedAssignments}
            onRemove={removeSelectedAssignment}
            onStudentSelect={handleStudentSelectClick}
          />
        </div>
      ) : (
        <div className="my-5 px-3 xl:px-0">
          <StudentSelectionPanel
            selectedAssignments={selectedAssignments}
            onInit={() => {
              setSelectedAssignmentIds(new Set());
              setOpenedBookIds(new Set([bookNodes[0].id]));
              setOpenedLessonIds(new Set());
            }}
            onBack={() => setStep('ASSIGNMENT')}
          />
        </div>
      )}
    </>
  );
};

export default CreateRecitationWrapper;
